/**
 * Utilitaires pour le calcul des périodes
 * Utilisé par tous les composants qui ont besoin de calculer des périodes de comparaison
 */

/**
 * Date au format `YYYY-MM-DD`, lue sur le fuseau du navigateur.
 *
 * `toISOString()` conviendrait pour un instant, pas pour une borne de calendrier. Les dates
 * ci-dessous sont construites en heure locale — `new Date(annee, mois, 1)` —, et à l'est de
 * Greenwich minuit local tombe la veille en UTC : le 1er septembre devenait le 31 août, et
 * « Ce mois » s'affichait comme commençant un jour trop tôt.
 *
 * Une borne de calendrier était donc fausse en permanence. Les périodes glissantes, elles,
 * conservaient l'heure courante : le décalage ne les touchait qu'entre minuit et le décalage
 * horaire — deux heures par nuit en été —, et il y reculait la fenêtre entière d'un jour, ce qui
 * les rendait fortuitement justes. Le défaut ne se manifestait donc jamais là où on l'aurait vu.
 */
const jour = (d) => {
  const mois = String(d.getMonth() + 1).padStart(2, '0');
  const quantieme = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mois}-${quantieme}`;
};

/**
 * Une `Date` rendue en `YYYY-MM-DD` **sur le fuseau du navigateur**.
 *
 * ⚠️ `toISOString().split('T')[0]` est le piège jumeau de [[dateLocale]], dans l'autre sens :
 * il rend le quantième **UTC**. À l'est de Greenwich, minuit local tombe la veille en UTC, et
 * `new Date(2026, 9, 29).toISOString()` annonce donc le 28. Toute date de calendrier destinée
 * au serveur ou à un `<input type="date">` passe par ici.
 */
export function jourIso(date) {
  return jour(date);
}

/** Le jour en cours sur le fuseau du navigateur. */
export function aujourdhuiIso() {
  return jour(new Date());
}

/**
 * Une date de calendrier lue à **minuit local**, et non à minuit UTC.
 *
 * `new Date('2026-10-16')` est interprété en UTC : à Paris, cela tombe à 02:00 locales en
 * heure d'été, 01:00 en heure d'hiver. Un curseur qui avance ensuite par
 * `setDate(getDate() + 1)` conserve son heure locale — c'est ce que fait `setDate` —, si
 * bien qu'au passage à l'heure d'hiver il **gagne une heure** sur une borne de fin restée,
 * elle, à minuit UTC. La boucle `curseur <= fin` s'arrête alors un jour trop tôt.
 *
 * Ce n'est pas théorique : le 30 octobre 2026, « 14 derniers jours » demandait bien le 16 au
 * 29 octobre — le serveur rendait les quatorze journées — et le graphique s'arrêtait au 28,
 * parce que la plage enjambe le changement d'heure du 25. Le défaut n'apparaît donc que
 * quelques semaines par an, et il retire la journée la plus regardée : la dernière.
 *
 * ⚠️ Toute borne de calendrier qui sert à **parcourir** des jours doit passer par ici.
 * `new Date(chaîne)` convient pour afficher un jour, pas pour en compter.
 */
export function dateLocale(iso) {
  if (iso instanceof Date) {
    return new Date(iso.getFullYear(), iso.getMonth(), iso.getDate());
  }
  const [annee, mois, quantieme] = String(iso).split('-').map(Number);
  if (!annee || !mois || !quantieme) return new Date(iso);
  return new Date(annee, mois - 1, quantieme);
}

/** Jours pleins couverts par les périodes glissantes, comme la table du serveur. */
const JOURS_GLISSANTS = {
  LAST_7_DAYS: 7,
  LAST_14_DAYS: 14,
  LAST_30_DAYS: 30,
  LAST_60_DAYS: 60,
  LAST_90_DAYS: 90,
  LAST_180_DAYS: 180,
  LAST_365_DAYS: 365
};

/**
 * Calcule les dates de début et fin pour une période nommée.
 *
 * **Les périodes nommées s'arrêtent hier.** La journée en cours est incomplète, et la mêler à
 * des journées pleines fausse la dernière colonne d'un tableau comme la moyenne d'une période.
 * Deux exceptions, qui n'ont de sens que par là : « Aujourd'hui », dont c'est l'objet même, et
 * « Personnalisée », où les bornes sont celles qu'on a saisies.
 *
 * **Ce calcul reproduit `bornes()` de `routes/dashboard/consolide.js`**, et doit le suivre. Le
 * dashboard consolidé envoie au serveur le *nom* de la période, pas les dates : celles affichées
 * ici ne servent qu'à montrer ce qui sera demandé. Quand les deux divergent, l'écran annonce une
 * plage et le tableau en montre une autre — c'est ce qui s'est produit, le sélecteur affichant
 * « 31/08 → 14/09 » pour un tableau qui portait le 01/09 au 13/09.
 *
 * @param {string} periodName - Le nom de la période (LAST_MONTH, THIS_MONTH, etc.)
 * @returns {{ startDate: string, endDate: string }}
 */
export function getDateRangeFromPeriod(periodName) {
  const aujourdhui = new Date();
  const hier = new Date(aujourdhui);
  hier.setDate(aujourdhui.getDate() - 1);

  /**
   * Une période à cheval sur son premier jour se réduit à ce jour-là.
   *
   * Le 1er du mois, « Ce mois » commencerait le 1er et finirait la veille — une plage à
   * l'envers, que le serveur traduit par un tableau vide sans dire pourquoi. Idem le 1er
   * janvier pour « Année en cours ».
   */
  const finApres = (debut) => (jour(hier) < debut ? debut : jour(hier));

  switch (periodName) {
    case 'TODAY':
      return { startDate: jour(aujourdhui), endDate: jour(aujourdhui) };

    case 'YESTERDAY':
      return { startDate: jour(hier), endDate: jour(hier) };

    case 'THIS_MONTH': {
      const debut = jour(new Date(aujourdhui.getFullYear(), aujourdhui.getMonth(), 1));
      return { startDate: debut, endDate: finApres(debut) };
    }

    case 'LAST_MONTH':
      return {
        startDate: jour(new Date(aujourdhui.getFullYear(), aujourdhui.getMonth() - 1, 1)),
        // Le jour 0 du mois courant est le dernier du mois précédent.
        endDate: jour(new Date(aujourdhui.getFullYear(), aujourdhui.getMonth(), 0))
      };

    case 'THIS_YEAR': {
      const debut = jour(new Date(aujourdhui.getFullYear(), 0, 1));
      return { startDate: debut, endDate: finApres(debut) };
    }

    case 'LAST_YEAR':
      return {
        startDate: jour(new Date(aujourdhui.getFullYear() - 1, 0, 1)),
        endDate: jour(new Date(aujourdhui.getFullYear() - 1, 11, 31))
      };

    default: {
      // Repli à trente jours, comme le serveur : une période inconnue vaut mieux affichée
      // à tort que pas affichée du tout.
      const nb = JOURS_GLISSANTS[periodName] || JOURS_GLISSANTS.LAST_30_DAYS;
      const debut = new Date(hier);
      debut.setDate(debut.getDate() - (nb - 1));
      return { startDate: jour(debut), endDate: jour(hier) };
    }
  }
}

/**
 * Nombre de journées couvertes par une période, bornes comprises.
 *
 * ⚠️ **Jamais `(fin - debut) / 86400000`.** Une période qui enjambe un changement d'heure ne
 * compte pas un nombre entier de millisecondes-jours : du 16 au 29 octobre 2026 il y a treize
 * jours **et une heure**, que `Math.ceil` arrondit à quatorze, et le `+ 1` d'usage porte le
 * compte à quinze. C'est ainsi que la période de comparaison gagnait silencieusement un jour
 * à chaque automne, et les tableaux comparaient quatorze journées à quinze.
 *
 * On compte donc des **quantièmes**, pas des durées : `Date.UTC` sur les champs locaux rend un
 * numéro de jour exact, que l'heure d'été ne touche pas.
 */
export function nombreDeJours(debut, fin) {
  const numero = (d) => Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86400000);
  return numero(dateLocale(fin)) - numero(dateLocale(debut)) + 1;
}

/**
 * Les bornes de la période regardée, qu'elle soit nommée ou saisie.
 *
 * Les écrans posaient tous les deux lignes « si CUSTOM, les dates saisies, sinon la table » —
 * et une bonne moitié d'entre eux avec leur propre table. C'est le seul endroit où la question
 * se tranche.
 *
 * @returns {{startDate: string, endDate: string}|null} `null` si une période saisie n'a pas
 *   ses deux bornes : il n'y a alors rien à demander au serveur, et surtout rien à comparer.
 */
export function bornesDeLaPeriodeChoisie(periodName, customStartDate, customEndDate) {
  if (periodName === 'CUSTOM') {
    if (!customStartDate || !customEndDate) return null;
    return { startDate: customStartDate, endDate: customEndDate };
  }
  return getDateRangeFromPeriod(periodName);
}

/**
 * Les bornes de la période à laquelle on compare.
 *
 * ⚠️ **Tout se fait à minuit local, de la lecture à l'écriture.** L'ancien calcul lisait ses
 * bornes en UTC (`new Date('2026-10-29')`), faisait son arithmétique sur les champs **locaux**
 * (`setDate`, `setFullYear`) et sérialisait de nouveau en UTC (`toISOString()`). Les trois
 * étapes sont justes séparément ; mêlées, elles décalent d'un jour dès qu'un changement
 * d'heure sépare les deux bornes — une fenêtre de sept jours ouverte le 2 novembre 2026
 * désignait le 21 octobre pour début de sa comparaison au lieu du 22.
 *
 * Le passage à l'heure d'**été** décale dans l'autre sens, et il est tout aussi faux : c'est
 * le mélange des deux repères qui est en cause, pas le sens du décalage.
 *
 * @param {string} startDate - début de la période regardée, `YYYY-MM-DD`
 * @param {string} endDate   - fin de la période regardée, `YYYY-MM-DD`
 * @param {string} comparisonType - `previous_period`, `previous_year`, ou `custom`
 * @returns {{ startDate: string, endDate: string }} bornes vides pour `custom`, dont les dates
 *   sont saisies et non calculées.
 */
export function calculateComparisonDates(startDate, endDate, comparisonType) {
  if (comparisonType === 'custom' || !startDate || !endDate) {
    return { startDate: '', endDate: '' };
  }

  if (comparisonType === 'previous_year') {
    // Mêmes quantièmes, un an plus tôt.
    const debut = dateLocale(startDate);
    const fin = dateLocale(endDate);
    debut.setFullYear(debut.getFullYear() - 1);
    fin.setFullYear(fin.getFullYear() - 1);
    return { startDate: jour(debut), endDate: jour(fin) };
  }

  // `previous_period` : la période de même durée qui précède immédiatement.
  const duree = nombreDeJours(startDate, endDate);
  const finPrecedente = dateLocale(startDate);
  finPrecedente.setDate(finPrecedente.getDate() - 1);
  const debutPrecedent = new Date(finPrecedente);
  debutPrecedent.setDate(debutPrecedent.getDate() - duree + 1);
  return { startDate: jour(debutPrecedent), endDate: jour(finPrecedente) };
}

/**
 * Les bornes de comparaison d'un **tableau**, qui ne reçoit pas les dates saisies.
 *
 * La différence avec [[calculateComparisonDates]] tient à un seul cas, et elle est
 * délibérée : la base « Personnalisée » y retombe sur la période précédente. Les tableaux de
 * dashboard n'ont pas les props `comparisonStartDate` / `comparisonEndDate` — seul le
 * sélecteur les porte —, et leur rendre des bornes vides reviendrait à leur faire demander au
 * serveur une plage sans dates. C'est le comportement qu'ils avaient tous, chacun chez soi ;
 * il est ici, écrit une fois et nommé.
 *
 * ⚠️ Le jour où ces tableaux recevront les dates saisies, c'est [[calculateComparisonDates]]
 * qu'ils devront appeler, et cette fonction n'aura plus de raison d'être.
 */
export function calculatePreviousPeriod(startDate, endDate, comparisonType = 'previous_period') {
  return calculateComparisonDates(
    startDate,
    endDate,
    comparisonType === 'previous_year' ? 'previous_year' : 'previous_period'
  );
}

/**
 * Les journées d'une période, bornes comprises, au format `YYYY-MM-DD`.
 *
 * Les deux bornes sont ramenées à minuit local par [[dateLocale]] : le curseur et la fin
 * restent alors sur la même heure, le changement d'heure compris.
 */
export function joursDeLaPeriode(debutIso, finIso) {
  const curseur = dateLocale(debutIso);
  const fin = dateLocale(finIso);
  const jours = [];
  while (curseur <= fin) {
    jours.push(jour(curseur));
    curseur.setDate(curseur.getDate() + 1);
  }
  return jours;
}
